import cn from 'classnames'
import Button from 'ext-ui/components/Button/Button'
import DropdownMenu from 'ext-ui/components/Dropdown/components/DropdownMenu/DropdownMenu'
import DropdownOption, {
  type DropdownOptionModel,
} from 'ext-ui/components/Dropdown/components/DropdownOption/DropdownOption'
import { useDropdownLogic } from 'ext-ui/components/Dropdown/hooks/useDropdownLogic'
import React, { useMemo } from 'react'
import SvgChevronUp from 'src/components/Svgs/SvgChevronUp'
import { useTranslate } from 'src/localization/Trns'
import type { BillPaymentDisplayStatus } from 'src/queries/payments/payments.types'

import { paymentStatusPresentation } from './paymentHistoryPresentation'
import style from './PaymentHistoryStatusDropdown.style'

interface PaymentHistoryStatusDropdownProps {
  statuses: BillPaymentDisplayStatus[]
  onSelect: (statuses: BillPaymentDisplayStatus[]) => void
}

const STATUS_OPTIONS: BillPaymentDisplayStatus[] = [
  'ACTION_REQUIRED',
  'PROCESSING',
  'COMPLETED',
  'FAILED',
  'RETURNED',
  'CANCELLED',
]

const PaymentHistoryStatusDropdown: React.FC<
  PaymentHistoryStatusDropdownProps
> = ({ statuses, onSelect }) => {
  const translate = useTranslate()
  const options = useMemo<
    DropdownOptionModel<BillPaymentDisplayStatus>[]
  >(
    () =>
      STATUS_OPTIONS.map((status) => ({
        value: status,
        label: translate(paymentStatusPresentation(status).label),
        details: status,
      })),
    [translate],
  )
  const selectedValues = useMemo(
    () =>
      options.filter(
        (option) => option.details && statuses.includes(option.details),
      ),
    [options, statuses],
  )
  const {
    rows,
    open,
    getDropdownHandleProps,
    getMenuProps,
    getItemProps,
    getSearchInputProps,
    containerRef,
    optimisticSelectedValues,
  } = useDropdownLogic<BillPaymentDisplayStatus>({
    groups: [{ id: 'statuses', options }],
    selectedValues,
    onSelect: ({ details: status }) => {
      if (!status) return
      onSelect(
        statuses.includes(status)
          ? statuses.filter((selectedStatus) => selectedStatus !== status)
          : [...statuses, status],
      )
    },
    noneLabel: '',
    multi: true,
  })
  const {
    ref: handleRef,
    onKeyDown: onHandleKeyDown,
    toggleButtonProps,
  } = getDropdownHandleProps<HTMLButtonElement>()
  const { width: _width, ...menuProps } = getMenuProps()
  const selectedStatuses = new Set(
    optimisticSelectedValues.map((value) => value.value),
  )

  return (
    <div
      ref={containerRef}
      className="payment-history-filter-dropdown"
      css={style}
    >
      <Button
        {...toggleButtonProps}
        ref={handleRef}
        className={cn('payment-history-status-handle', {
          selected: statuses.length > 0,
        })}
        data-testid="payment-history-status-filter"
        dropShadow={false}
        tabIndex={0}
        type="button"
        variant="toolbar"
        onKeyDown={onHandleKeyDown}
      >
        <span className="status-label">
          {statuses.length === 0
            ? translate('All statuses')
            : translate('${count} statuses', { count: statuses.length })}
        </span>
        <SvgChevronUp
          className={cn('chevron', { open })}
          fill="currentColor"
          height={16}
          width={16}
        />
      </Button>
      <DropdownMenu
        {...menuProps}
        className="payment-history-filter-menu"
        container={containerRef.current}
        dropdownOptionsCount={6}
        menuTestId="payment-history-status-filter"
        open={open}
        searchProps={getSearchInputProps()}
        searchable={false}
        variant="small"
        width={280}
      >
        {rows.map((row, index) => {
          if (row.kind !== 'option') return null
          const option = row.option
          return (
            <DropdownOption
              key={option.value}
              {...option}
              {...getItemProps({ item: row, index })}
              data-testid={`payment-history-status-${option.value.toLowerCase()}`}
              multi
              selected={selectedStatuses.has(option.value)}
              variant="small"
            />
          )
        })}
      </DropdownMenu>
    </div>
  )
}

export default PaymentHistoryStatusDropdown
